import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, AlertTriangle, FileWarning } from "lucide-react";
import { toast } from "sonner";

export default function ProfessionalProfile() {
  const { user } = useAuth();
  const [pro, setPro] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    if (!user?.professional_id) return;
    const { data } = await api.get(`/professionals/${user.professional_id}`);
    setPro(data);
  };
  useEffect(() => { load(); }, [user]);

  const save = async () => {
    try {
      await api.patch(`/professionals/${pro.id}`, pro);
      toast.success("Profile updated");
      setEditing(false);
    } catch { toast.error("Failed"); }
  };

  if (!pro) return <div className="text-slate-500">Loading…</div>;

  const today = new Date();
  const expiringSoon = (pro.credentials || []).filter((c) => {
    if (!c.expiry) return false;
    const d = new Date(c.expiry);
    const daysLeft = (d - today) / (1000 * 60 * 60 * 24);
    return daysLeft < 90;
  });

  return (
    <div className="space-y-4" data-testid="pro-profile-page">
      <h1 className="text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>My Profile</h1>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Personal</div>
            <Button size="sm" variant="ghost" onClick={() => editing ? save() : setEditing(true)} data-testid="pro-profile-edit-btn">
              {editing ? "Save" : "Edit"}
            </Button>
          </div>
          {editing ? (
            <div className="space-y-3">
              <div><Label>Phone</Label><Input value={pro.phone || ""} onChange={(e) => setPro({ ...pro, phone: e.target.value })} /></div>
              <div><Label>City</Label><Input value={pro.city || ""} onChange={(e) => setPro({ ...pro, city: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Hourly rate</Label><Input type="number" value={pro.hourly_rate} onChange={(e) => setPro({ ...pro, hourly_rate: Number(e.target.value) })} data-testid="pro-edit-rate" /></div>
                <div><Label>Weekend rate</Label><Input type="number" value={pro.weekend_rate} onChange={(e) => setPro({ ...pro, weekend_rate: Number(e.target.value) })} /></div>
              </div>
              <div><Label>Bio</Label><Textarea value={pro.bio || ""} onChange={(e) => setPro({ ...pro, bio: e.target.value })} /></div>
            </div>
          ) : (
            <div className="text-sm space-y-1 text-slate-700">
              <div>📞 {pro.phone}</div>
              <div>📍 {pro.city}, {pro.province}</div>
              <div>💼 {pro.primary_position} · {pro.experience_years}+ years</div>
              <div>💰 ${pro.hourly_rate}/hr · Weekend ${pro.weekend_rate}/hr</div>
              {pro.bio && <p className="text-slate-600 mt-2">{pro.bio}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand-teal" /> Credentials
          </div>
          <div className="space-y-2">
            {(pro.credentials || []).map((c, i) => {
              const d = c.expiry ? new Date(c.expiry) : null;
              const daysLeft = d ? Math.floor((d - today) / (1000 * 60 * 60 * 24)) : null;
              const expiring = daysLeft != null && daysLeft < 90;
              return (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-50">
                  <div>
                    <div className="text-sm font-medium">{c.type}</div>
                    <div className="text-xs text-slate-500">Expires {c.expiry}</div>
                  </div>
                  {expiring ? (
                    <span className="status-pill bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />{daysLeft}d</span>
                  ) : (
                    <span className="status-pill bg-green-100 text-green-700">{c.status}</span>
                  )}
                </div>
              );
            })}
          </div>
          {expiringSoon.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              {expiringSoon.length} credential{expiringSoon.length === 1 ? "" : "s"} expiring soon. Please update.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
